import {
  AggregationType,
  type MetricDefinition,
} from "~/ui-rtk/constants/charts"

// The regional cube (cube/model/company_regional_metrics_daily.js) over the
// ClickHouse `regional_company_data_daily` snapshot; its capability row is in
// db_capabilities.js. Every key below is `<cube>.<measure>`.
export const REGIONAL_CUBE = "company_regional_metrics_daily"
export const REGIONAL_DATE_DIMENSION = `${REGIONAL_CUBE}.date`
export const REGIONAL_COUNTRY_DIMENSION = `${REGIONAL_CUBE}.country`

const measure = (name: string) => `${REGIONAL_CUBE}.${name}`

/**
 * A regional metric group rendered as one `KPITable`. Mirrors the dashboard's
 * builtin group shape (title + metrics + marginal footer config) but every key
 * references the regional cube. The list is deliberately trimmed to what the
 * pipeline produces at country grain and is kept local to this view — it is
 * NOT part of the custom-report catalog.
 */
export type RegionalMetricGroup = {
  key: string
  title: string
  metrics: MetricDefinition[]
  /** Period-over-period marginal footer; `marginalMetrics` restricts its sum. */
  showTotals?: boolean
  marginalMetrics?: string[]
  marginalLabel?: Record<AggregationType, string>
}

const MARGINAL_LABEL: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "DoD Marginal",
  [AggregationType.WEEKLY]: "WoW Marginal",
  [AggregationType.MONTHLY]: "MoM Marginal",
}

const MARGINAL_SPEND_LABEL: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "DoD Marginal Spend",
  [AggregationType.WEEKLY]: "WoW Marginal Spend",
  [AggregationType.MONTHLY]: "MoM Marginal Spend",
}

export const REGIONAL_METRIC_GROUPS: RegionalMetricGroup[] = [
  {
    key: "regionalAdSpend",
    title: "Ad Spend",
    metrics: [
      {
        key: measure("total_ad_spend"),
        label: "Total Ad Spend",
        type: "currency",
      },
      {
        key: measure("facebook_ads_spend"),
        label: "Facebook Ads Spend",
        type: "currency",
      },
      {
        key: measure("instagram_ads_spend"),
        label: "Instagram Ads Spend",
        type: "currency",
      },
      {
        key: measure("tiktok_ads_spend"),
        label: "TikTok Ads Spend",
        type: "currency",
      },
      {
        key: measure("pinterest_ads_spend"),
        label: "Pinterest Ads Spend",
        type: "currency",
      },
      {
        key: measure("google_ads_spend"),
        label: "Google Ads Spend",
        type: "currency",
      },
    ],
    showTotals: true,
    marginalMetrics: [measure("total_ad_spend")],
    marginalLabel: MARGINAL_SPEND_LABEL,
  },
  {
    key: "regionalRevenue",
    title: "Revenue",
    metrics: [
      {
        key: measure("total_revenue"),
        label: "Total Revenue",
        type: "currency",
      },
      { key: measure("total_orders"), label: "Total Orders", type: "number" },
    ],
    showTotals: true,
    marginalMetrics: [measure("total_revenue")],
    marginalLabel: MARGINAL_LABEL,
  },
  {
    key: "regionalTraffic",
    title: "Traffic",
    metrics: [
      {
        key: measure("ga_total_sessions"),
        label: "GA Total Sessions",
        type: "number",
      },
      {
        key: measure("ga_total_revenue"),
        label: "GA Total Revenue",
        type: "currency",
      },
    ],
    showTotals: true,
    marginalMetrics: [measure("ga_total_sessions")],
    marginalLabel: MARGINAL_LABEL,
  },
  {
    key: "regionalPerSessionValue",
    title: "Per Session Value",
    metrics: [
      {
        key: measure("per_session_value"),
        label: "Per Session Value",
        type: "currency",
      },
    ],
  },
  {
    key: "regionalGrossProfit",
    title: "Gross Profit",
    metrics: [
      { key: measure("gross_profit"), label: "Gross Profit", type: "currency" },
    ],
  },
  {
    key: "regionalContribution",
    title: "Contribution",
    metrics: [
      {
        key: measure("total_contribution_margin"),
        label: "Total Contribution Margin",
        type: "currency",
      },
    ],
  },
]

/** Every measure the regional query requests (one query serves all groups). */
export const REGIONAL_KPI_MEASURES: string[] = REGIONAL_METRIC_GROUPS.flatMap(
  group => group.metrics.map(metric => metric.key),
)
